/**
 * NavCustomizer — Bottom sheet for paid accounts to customize their bottom nav tabs.
 * Lets the user pick up to 4 tabs from a role-specific pool.
 * The 5th slot is always the Menu button (fixed, not customizable).
 * Selection order determines display order.
 */
import { useState } from 'react';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useThemeColors } from '../context/ThemeContext';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { useTranslation } from 'react-i18next';
import { snapshotMigrationConfig } from '../utils/migrationConfig';
import { setNavConfig as apiSetNavConfig, resetNavConfig as apiResetNavConfig } from '../api/users';
import {
  Home, Car, Calendar, Store, Users, BarChart3, MessageCircle,
  ShoppingCart, Trophy, Bell, Package, Wrench, X, Check,
  Star, CreditCard, Eye
} from 'lucide-react';

const MAX_TABS = 4;

// Pool of available tabs per role
const WORKSHOP_POOL = [
  { id: 'home',        labelKey: 'nav.home',             Icon: Home,          path: '/workshop/dashboard', matchPaths: ['/workshop/dashboard'] },
  { id: 'appointments',labelKey: 'nav.appointments',     Icon: Calendar,      path: '/workshop/appointments', matchPaths: ['/workshop/appointments'] },
  { id: 'store',       labelKey: 'workshopStore.title',  Icon: Store,         path: '/workshop/store', matchPaths: ['/workshop/store', '/workshop/products', '/workshop/orders', '/marketplace', '/product/'] },
  { id: 'earnings',    labelKey: 'workshopEarnings.title',    Icon: BarChart3, path: '/workshop/earnings', matchPaths: ['/workshop/earnings'] },
  { id: 'messages',    labelKey: 'nav.messages',         Icon: MessageCircle, path: '/messages', matchPaths: ['/messages'] },
  { id: 'marketplace', labelKey: 'nav.marketplace',      Icon: ShoppingCart,  path: '/marketplace', matchPaths: ['/marketplace', '/product/'] },
  { id: 'rankings',    labelKey: 'nav.rankings',         Icon: Trophy,        path: '/rankings', matchPaths: ['/rankings'] },
  { id: 'inbox',       labelKey: 'inbox.title',          Icon: Bell,          path: '/inbox', matchPaths: ['/inbox'] },
  { id: 'clients',     labelKey: 'workshopClients.title',Icon: Users,         path: '/workshop/clients', matchPaths: ['/workshop/clients'] },
  { id: 'reviews',     labelKey: 'workshopReviews.title',Icon: Star,          path: '/workshop/reviews', matchPaths: ['/workshop/reviews'] },
  { id: 'profileViews',labelKey: 'profileViews.title',  Icon: Eye,           path: '/workshop/profile-views', matchPaths: ['/workshop/profile-views'] },
  { id: 'orders',      labelKey: 'nav.workshopOrders',   Icon: Package,       path: '/workshop/orders', matchPaths: ['/workshop/orders'] },
  { id: 'billing',     labelKey: 'workshopBilling.title',Icon: CreditCard,    path: '/workshop/billing', matchPaths: ['/workshop/billing'] },
];

const MECHANIC_POOL = [
  { id: 'home',        labelKey: 'nav.home',             Icon: Home,          path: '/mechanic/dashboard', matchPaths: ['/mechanic/dashboard'] },
  { id: 'appointments',labelKey: 'nav.appointments',     Icon: Calendar,      path: '/mechanic/appointments', matchPaths: ['/mechanic/appointments', '/mechanic/appointment/'] },
  { id: 'setup',       labelKey: 'nav.myWorkshop',       Icon: Wrench,        path: '/mechanic/setup', matchPaths: ['/mechanic/setup'] },
  { id: 'vehicles',    labelKey: 'nav.vehicles',         Icon: Car,           path: '/vehicles', matchPaths: ['/vehicles', '/vehicle/'] },
  { id: 'messages',    labelKey: 'nav.messages',         Icon: MessageCircle, path: '/messages', matchPaths: ['/messages'] },
  { id: 'marketplace', labelKey: 'nav.marketplace',      Icon: ShoppingCart,  path: '/marketplace', matchPaths: ['/marketplace', '/product/'] },
  { id: 'rankings',    labelKey: 'nav.rankings',         Icon: Trophy,        path: '/rankings', matchPaths: ['/rankings'] },
  { id: 'inbox',       labelKey: 'inbox.title',          Icon: Bell,          path: '/inbox', matchPaths: ['/inbox'] },
  { id: 'billing',     labelKey: 'mechBilling.title',    Icon: CreditCard,    path: '/mechanic/billing', matchPaths: ['/mechanic/billing'] },
];

export function getPoolForRole(role) {
  if (role === 'workshop_owner') return WORKSHOP_POOL;
  if (role === 'mechanic') return MECHANIC_POOL;
  return [];
}

export function buildCustomTabs(navConfig, role, t) {
  const pool = getPoolForRole(role);
  return navConfig
    .map(id => pool.find(p => p.id === id))
    .filter(Boolean)
    .map(p => ({ ...p, label: t(p.labelKey) }));
}

export default function NavCustomizer({ role, currentConfig, onClose, onSaved }) {
  const { t } = useTranslation();
  const { refreshProfile } = useAuth();
  const { isDark } = useThemeColors();
  const tokens = isDark ? dsv2 : dsv2Light;

  const pool = getPoolForRole(role);
  const [selected, setSelected] = useState(currentConfig || []);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= MAX_TABS) return prev; // max reached
      return [...prev, id];
    });
  };

  // ──────────────────────────────────────────────────────────────────────────
  // EL MENÚ SE GUARDA POR (USUARIO, ROL). El API tiene la ruta desde el
  // 2026-08-10: `PUT /users/me/nav-config` con el rol en el CUERPO, y
  // `DELETE` para volver al orden de fábrica.
  //
  // (Este recuadro decía antes que `navConfig` se quedaba en Firestore porque el
  // API no tenía ruta ni esquema. Ya los tiene. Lo que sigue en pie es que NO se
  // puede colar en `PATCH /users/me`: esa lista de permitidos responde 400
  // NOMBRANDO el campo que sobra, o sea que tumbaría el guardado entero en vez
  // de ignorarlo — comprobado contra el API real.)
  //
  // 🔴 SE DEJA DE GUARDAR EL ARRAY PLANO, Y ES UN ARREGLO, NO UN EFECTO SECUNDARIO.
  //
  // Hasta hoy esta pantalla escribía `navConfig: [ids]` sin decir de qué rol, y
  // `configForRole` (utils/navItems.js:113) DESCARTA esa forma justamente para no
  // aplicarle a un taller el menú que ordenó como cliente. O sea: el usuario
  // pulsaba "Guardar", se cerraba la hoja, y nadie volvía a mirar lo guardado.
  // La forma plana además ya no es representable — el contrato exige el rol y
  // responde 400 sin él —, así que mantenerla habría dejado los dos caminos
  // guardando cosas distintas. Ahora los dos escriben por rol, que es lo que la
  // barra (`BottomNav.saveNavConfig`) ya hacía bien.
  //
  // `DELETE` borra el orden de TODOS los roles, igual que el `navConfig: null`
  // de Firestore. Es lo que dice el botón: volver al orden de fábrica.
  // ──────────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    try {
      const snap = await snapshotMigrationConfig();
      if (snap.readsFrom('users') === 'postgres') await apiSetNavConfig(role, selected);
      else await updateDoc(doc(db, 'users', auth.currentUser.uid), { [`navConfig.${role}`]: selected });
      await refreshProfile();
      onSaved?.();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const snap = await snapshotMigrationConfig();
      if (snap.readsFrom('users') === 'postgres') await apiResetNavConfig();
      else await updateDoc(doc(db, 'users', auth.currentUser.uid), { navConfig: null });
      await refreshProfile();
      onSaved?.();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      backgroundColor: 'rgba(0,0,0,0.55)',
      zIndex: 2000,
      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        backgroundColor: tokens.surface,
        borderRadius: '24px 24px 0 0',
        maxHeight: '85vh',
        display: 'flex', flexDirection: 'column',
        border: `1px solid ${tokens.outline}`,
        boxShadow: tokens.shadow.lg,
        animation: 'slideUp 0.25s ease',
      }}>
        {/* Handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
          <div style={{ width: '36px', height: '4px', backgroundColor: tokens.outline, borderRadius: '2px' }} />
        </div>

        {/* Header */}
        <div style={{ padding: '12px 20px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${tokens.outline}` }}>
          <div>
            <div style={{ fontSize: '17px', fontWeight: '700', color: tokens.text, fontFamily: tokens.font.head }}>
              {t('nav.customizeNav')}
            </div>
            <div style={{ fontSize: '13px', color: tokens.textMuted, marginTop: '2px' }}>
              {t('nav.customizeNavDesc', { count: selected.length, max: MAX_TABS })}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: tokens.textMuted }}>
            <X size={22} />
          </button>
        </div>

        {/* Preview strip */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${tokens.outline}`, display: 'flex', gap: '8px', alignItems: 'center' }}>
          {Array.from({ length: MAX_TABS }).map((_, i) => {
            const tabId = selected[i];
            const tab = tabId ? pool.find(p => p.id === tabId) : null;
            const TabIcon = tab?.Icon;
            return (
              <div key={i} style={{
                flex: 1, padding: '10px 4px',
                borderRadius: '12px',
                border: tab ? `1.5px solid ${tokens.redBorder}` : `1.5px dashed ${tokens.outline}`,
                background: tab ? tokens.redSoft : 'transparent',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                transition: 'all 0.2s',
                cursor: tab ? 'pointer' : 'default',
              }}
                onClick={() => tab && toggle(tab.id)}
              >
                {TabIcon
                  ? <TabIcon size={18} color={tokens.red} />
                  : <div style={{ width: '18px', height: '18px', borderRadius: '4px', background: tokens.surfaceLow }} />
                }
                <span style={{ fontSize: '9px', fontWeight: '600', color: tab ? tokens.red : tokens.textDim, textAlign: 'center', lineHeight: '1.2' }}>
                  {tab ? t(tab.labelKey) : `${i + 1}`}
                </span>
              </div>
            );
          })}
          {/* Menu (always fixed) */}
          <div style={{
            flex: 1, padding: '10px 4px',
            borderRadius: '12px',
            border: `1px solid ${tokens.outline}`,
            background: tokens.surfaceLow,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
          }}>
            <div style={{ width: '18px', height: '18px', display: 'flex', flexDirection: 'column', gap: '3px', justifyContent: 'center' }}>
              {[0,1,2].map(i => <div key={i} style={{ height: '2px', background: tokens.textDim, borderRadius: '1px' }} />)}
            </div>
            <span style={{ fontSize: '9px', fontWeight: '600', color: tokens.textDim }}>{t('nav.menu')}</span>
          </div>
        </div>

        {/* Pool grid */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
            {pool.map(tab => {
              const TabIcon = tab.Icon;
              const selIdx = selected.indexOf(tab.id);
              const isSel = selIdx !== -1;
              const isDisabled = !isSel && selected.length >= MAX_TABS;

              return (
                <button
                  key={tab.id}
                  onClick={() => !isDisabled && toggle(tab.id)}
                  style={{
                    padding: '14px 8px',
                    borderRadius: '14px',
                    border: isSel ? `1.5px solid ${tokens.redBorder}` : `1px solid ${tokens.outline}`,
                    background: isSel ? tokens.redSoft : tokens.surfaceLow,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.4 : 1,
                    position: 'relative',
                    transition: 'all 0.18s',
                  }}
                >
                  {isSel && (
                    <div style={{
                      position: 'absolute', top: '6px', right: '6px',
                      width: '18px', height: '18px', borderRadius: '50%',
                      background: tokens.red,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '10px', fontWeight: '800', color: '#fff',
                      fontFamily: tokens.font.mono,
                    }}>
                      {selIdx + 1}
                    </div>
                  )}
                  <TabIcon size={22} color={isSel ? tokens.red : tokens.textMuted} />
                  <span style={{
                    fontSize: '11px', fontWeight: isSel ? '700' : '500',
                    color: isSel ? tokens.red : tokens.textMuted,
                    textAlign: 'center', lineHeight: '1.3',
                  }}>
                    {t(tab.labelKey)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer actions */}
        <div style={{ padding: '14px 20px', paddingBottom: 'calc(14px + env(safe-area-inset-bottom))', borderTop: `1px solid ${tokens.outline}`, display: 'flex', gap: '10px' }}>
          <button
            onClick={handleReset}
            disabled={saving}
            style={{
              padding: '13px 16px', borderRadius: '12px',
              border: `1px solid ${tokens.outline}`,
              background: 'none', fontSize: '14px', fontWeight: '600',
              color: tokens.textMuted, cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {t('nav.navReset')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || selected.length === 0}
            style={{
              flex: 1, padding: '13px',
              borderRadius: '12px', border: 'none',
              background: selected.length === 0 ? tokens.surfaceHigh : tokens.red,
              fontSize: '14px', fontWeight: '700',
              color: selected.length === 0 ? tokens.textDim : '#fff',
              cursor: selected.length === 0 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              transition: 'all 0.2s',
            }}
          >
            {saving ? t('common.saving') : <><Check size={16} /> {t('common.save')}</>}
          </button>
        </div>
      </div>
    </div>
  );
}
